import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import authFetch from '../api/authFetch';
import './AstroInsight.css';

// ── API base ───────────────────────────────────────────────────
const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' ? '/api' : `http://${h}:5001/api`;
};

// ── Constants ──────────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const NAKSHATRA_ELEMENTS = {
  'Ashwini':'Fire','Bharani':'Fire','Krittika':'Fire',
  'Rohini':'Earth','Mrigashira':'Earth','Ardra':'Air',
  'Punarvasu':'Air','Pushya':'Water','Ashlesha':'Water',
  'Magha':'Fire','Purva Phalguni':'Fire','Uttara Phalguni':'Fire',
  'Hasta':'Earth','Chitra':'Earth','Swati':'Air',
  'Vishakha':'Air','Anuradha':'Water','Jyeshtha':'Water',
  'Mula':'Fire','Purva Ashadha':'Fire','Uttara Ashadha':'Fire',
  'Shravana':'Earth','Dhanishta':'Earth','Shatabhisha':'Air',
  'Purva Bhadrapada':'Air','Uttara Bhadrapada':'Water','Revati':'Water',
};

const MOON_EMOJIS = {
  'New Moon':'🌑','Waxing Crescent':'🌒','First Quarter':'🌓',
  'Waxing Gibbous':'🌔','Full Moon':'🌕','Waning Gibbous':'🌖',
  'Last Quarter':'🌗','Waning Crescent':'🌘',
};

// ── Helpers ────────────────────────────────────────────────────
function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function fmtDateLong(d) {
  return d.toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' });
}

function parseBhadraDate(str) {
  try {
    const m = str.match(/(\d{2})\s+(\w+)\s+(\d{4})/);
    if (!m) return null;
    const monthMap = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
    const mo = monthMap[m[2]];
    if (mo === undefined) return null;
    return new Date(parseInt(m[3]), mo, parseInt(m[1]));
  } catch { return null; }
}

function nakshatraBadgeClass(name, nakshatraTypes) {
  const nType = nakshatraTypes[name];
  if (nType) {
    return nType.toLowerCase()
      .replace(/\s+/g, '-')
      .replace('bullish-day-nifty-/-banknifty', 'bullish-day')
      .replace('brearish-day-nifty-/-banknifty', 'bearish-day');
  }
  return (NAKSHATRA_ELEMENTS[name] || 'Fire').toLowerCase();
}

// ── Sub-components ─────────────────────────────────────────────

// Badge
const Badge = ({ type, children }) => (
  <span className={`ua-badge ua-badge-${type}`}>{children}</span>
);

// Detail panel — empty state
const DetailEmpty = () => (
  <div className="ua-empty-state">
    <div className="ua-empty-icon">📅</div>
    <h3 style={{ fontSize:'0.95rem', color:'#e2e8f0' }}>Select a Date</h3>
    <p style={{ fontSize:'0.8rem' }}>Click any date to view complete astrology data</p>
  </div>
);

// Detail panel — loading
const DetailLoading = ({ date }) => (
  <>
    <div className="ua-detail-header">
      <div className="ua-detail-header-content">
        <h2>{fmtDateLong(date)}</h2>
        <div className="ua-detail-date">{fmtDate(date)}</div>
      </div>
    </div>
    <div className="ua-detail-loading">
      <div className="ua-detail-loading-icon">⏳</div>
      <div>Loading all astrology data…</div>
    </div>
  </>
);

// Detail panel — populated
const DetailContent = ({ date, panchangam, moonPhase, bhadra, sunDegree, astroEvents }) => {
  const dateStr = fmtDate(date);

  const dayBhadra = bhadra.filter(b => {
    const sd = parseBhadraDate(b.start);
    if (!sd) return false;
    return sd.getFullYear() === date.getFullYear() &&
           sd.getMonth()    === date.getMonth()    &&
           sd.getDate()     === date.getDate();
  });

  return (
    <>
      {/* Header */}
      <div className="ua-detail-header">
        <div className="ua-detail-header-content">
          <h2>{fmtDateLong(date)}</h2>
          <div className="ua-detail-date">
            {dateStr}{moonPhase ? ` • ${moonPhase.phaseName}` : ''}
          </div>
        </div>
        {moonPhase?.emoji && (
          <div className="ua-detail-header-emoji">{moonPhase.emoji}</div>
        )}
      </div>

      {/* Events & Festivals */}
      {(astroEvents.events.length > 0 || astroEvents.festivals.length > 0) && (
        <div className="ua-detail-section">
          <div className="ua-section-title">
            <span>📅</span> Events &amp; Festivals
          </div>
          {astroEvents.events.length > 0 && (
            <div className="ua-event-list">
              {astroEvents.events.map((evt, i) => {
                const bc = evt.eventType.toLowerCase().replace(/\s+/g,'-').replace('as-per-trend','trend');
                return (
                  <div key={i} className="ua-event-card">
                    <div className="ua-event-card-header">
                      <Badge type={bc}>{evt.eventType}</Badge>
                      {evt.moonPhase && (
                        <span style={{ fontSize:'0.9rem' }}>
                          {MOON_EMOJIS[evt.moonPhase] || '🌙'}
                        </span>
                      )}
                    </div>
                    {evt.stockName && <div className="ua-event-title">{evt.stockName}</div>}
                    {evt.description && <div className="ua-event-description">{evt.description}</div>}
                  </div>
                );
              })}
            </div>
          )}
          {astroEvents.festivals.length > 0 && (
            <div className="ua-event-list" style={{ marginTop: astroEvents.events.length ? '0.4rem' : 0 }}>
              {astroEvents.festivals.map((f, i) => (
                <div key={i} className="ua-event-card festival-card">
                  <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
                    <span>🎪</span>
                    <div>
                      <div className="ua-event-title">{f.name}</div>
                      {f.isMarketHoliday && <Badge type="warning">Market Holiday</Badge>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Market Timing */}
      <div className="ua-detail-section">
        <div className="ua-section-title">
          <span>⚠️</span> Market Timing Analysis
        </div>
        <div className="ua-detail-grid-2">
          {sunDegree && (<>
            <div className="ua-detail-item">
              <div className="ua-detail-label">Sun Degree</div>
              <div className="ua-detail-value">{sunDegree.sunDegree?.toFixed(2)}°</div>
            </div>
            <div className="ua-detail-item">
              <div className="ua-detail-label">Reversal Date</div>
              <div className="ua-detail-value">
                {sunDegree.isReversal
                  ? <Badge type="yes">Yes</Badge>
                  : <Badge type="no">No</Badge>}
              </div>
            </div>
          </>)}
          <div className="ua-detail-item">
            <div className="ua-detail-label">Time Trend Shift</div>
            <div className="ua-detail-value">
              {dayBhadra.length > 0
                ? <Badge type="warning">Active</Badge>
                : <Badge type="no">None</Badge>}
            </div>
          </div>
          {dayBhadra.length > 0 && (
            <div className="ua-detail-item">
              <div className="ua-detail-label">Period</div>
              <div className="ua-detail-value" style={{ fontSize:'0.7rem' }}>
                {dayBhadra[0].start?.split(' at ')[1]} – {dayBhadra[0].end?.split(' at ')[1]}
              </div>
            </div>
          )}
        </div>
        {(sunDegree?.isReversal || dayBhadra.length > 0) && (
          <div className="ua-alert-box">
            {sunDegree?.isReversal && '🌀 Gann Reversal Alert: Watch for trend changes'}
            {sunDegree?.isReversal && dayBhadra.length > 0 && ' | '}
            {dayBhadra.length > 0 && '⚠️ Avoid new trades during Time Trend Shift'}
          </div>
        )}
      </div>

      {/* Vedic Calendar & Moon */}
      {((panchangam && panchangam.success) || moonPhase) && (
        <div className="ua-detail-section">
          <div className="ua-section-title">
            <span>🕉️</span> Vedic Calendar &amp; Moon Phase
          </div>
          <div className="ua-detail-grid">
            {panchangam?.success && (<>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Nakshatra</div>
                <div className="ua-detail-value">{panchangam.nakshatra?.name}</div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Pada</div>
                <div className="ua-detail-value">{panchangam.nakshatra?.pada}</div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Lord</div>
                <div className="ua-detail-value">{panchangam.nakshatra?.lord}</div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Rashi</div>
                <div className="ua-detail-value">{panchangam.rashi?.name || panchangam.rashi}</div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Tithi</div>
                <div className="ua-detail-value">{panchangam.tithi?.name || panchangam.tithi}</div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Paksha</div>
                <div className="ua-detail-value">{panchangam.tithi?.paksha || 'N/A'}</div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Yoga</div>
                <div className="ua-detail-value">{panchangam.yoga?.name || panchangam.yoga}</div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Karana</div>
                <div className="ua-detail-value">
                  {panchangam.karana?.name || panchangam.karana || 'N/A'}
                  {panchangam.karana?.isVishti ? ' ⚠️' : ''}
                </div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Vara</div>
                <div className="ua-detail-value">{panchangam.vara}</div>
              </div>
            </>)}
            {moonPhase && (<>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Moon Phase</div>
                <div className="ua-detail-value">{moonPhase.emoji} {moonPhase.phaseName}</div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Illumination</div>
                <div className="ua-detail-value">{moonPhase.illumination}%</div>
              </div>
              <div className="ua-detail-item">
                <div className="ua-detail-label">Moon Age</div>
                <div className="ua-detail-value">{moonPhase.ageInDays} days</div>
              </div>
            </>)}
          </div>
        </div>
      )}
    </>
  );
};

// ── DayCell — pure display, no fetching ─────────────────────────
const DayCell = ({ day, month, year, otherMonth, isToday, isSelected, nakshatraTypes, monthData, dayData, onClick }) => {
  const dateObj = new Date(year, month, day);
  const dateStr = fmtDate(dateObj);
  const isSunday = dateObj.getDay() === 0;

  let cls = 'ua-day-cell';
  if (otherMonth) cls += ' other-month';
  if (isToday)    cls += ' today';
  if (isSelected) cls += ' selected';
  if (isSunday)   cls += ' sunday';

  // Other-month cells: just show the number, no click
  if (otherMonth) {
    return <div className={cls}><div className="ua-day-number">{day}</div></div>;
  }

  const loading = !dayData;
  const events = monthData?.astroEvents?.[dateStr] || { events: [], festivals: [] };

  const hasBhadra = (monthData?.bhadra || []).some(b => {
    const sd = parseBhadraDate(b.start);
    if (!sd) return false;
    return sd.getFullYear() === dateObj.getFullYear() &&
           sd.getMonth()    === dateObj.getMonth()    &&
           sd.getDate()     === dateObj.getDate();
  });

  const hasReversal = (monthData?.sunDegreeReversals || []).some(r => r.dateStr === dateStr);

  return (
    <div className={cls} onClick={() => onClick(dateObj)}>
      <div className="ua-day-header">
        <div className="ua-day-number">{day}</div>
        {!loading && (
          <div className="ua-day-header-icons">
            {dayData?.moon?.emoji && (
              <span className="ua-moon-emoji" title={dayData.moon.phaseName}>
                {dayData.moon.emoji}
              </span>
            )}
            {hasBhadra && <span className="ua-indicator-icon" title="Time Trend Shift">⚠️</span>}
            {hasReversal && <span className="ua-indicator-icon" title="Sun Degree Reversal">🌀</span>}
          </div>
        )}
      </div>
      <div className="ua-micro-indicators">
        {loading ? (
          <div className="ua-loading-indicator">…</div>
        ) : (
          <>
            <div className="ua-indicator-row">
              {dayData?.panchangam?.success && dayData.panchangam.nakshatra && (() => {
                const name = dayData.panchangam.nakshatra.name;
                const badgeCls = nakshatraBadgeClass(name, nakshatraTypes);
                return (
                  <span className={`ua-nakshatra-badge ${badgeCls}`} title={name}>
                    {name.replace(' ', '\u00A0')}
                  </span>
                );
              })()}
            </div>
            <div className="ua-indicator-row">
              {events.events.map((evt, i) => {
                const t = evt.eventType.toLowerCase().replace(/\s+/g,'-').replace('as-per-trend','trend');
                return <span key={i} className={`ua-event-dot ${t}`} title={evt.eventType} />;
              })}
              {events.festivals.length > 0 && (
                <span className="ua-event-dot festival" title={events.festivals[0].name} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════════
const AstroInsight = () => {
  const today = new Date();

  const [viewDate, setViewDate]           = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate]   = useState(today);
  const [nakshatraTypes, setNakshatraTypes] = useState({});

  // All data stored in state — no refs, no key-bumping
  const [monthData, setMonthData]     = useState({ bhadra: [], astroEvents: {}, sunDegreeReversals: [] });
  const [dayDataCache, setDayDataCache] = useState({}); // { [dateStr]: { panchangam, moon, sunDegree } }

  // Detail panel
  const [detailState, setDetailState] = useState('loading');
  const [detailData, setDetailData]   = useState(null);

  // ── Load nakshatra types once ──────────────────────────────────
  useEffect(() => {
    authFetch(`${getApiBase()}/nakshatra-stocks/types`)
      .then(r => r.json())
      .then(data => { if (data.success && data.mapping) setNakshatraTypes(data.mapping); })
      .catch(() => {});
  }, []);

  // ── Load ALL month data when viewDate changes ──────────────────
  useEffect(() => {
    // Clear previous data immediately so cells show loading state
    setDayDataCache({});
    setMonthData({ bhadra: [], astroEvents: {}, sunDegreeReversals: [] });

    const yr = viewDate.getFullYear();
    const mo = viewDate.getMonth() + 1; // 1-based for API
    const daysInMonth = new Date(yr, mo, 0).getDate();

    const load = async () => {
      // 1. Month-level: bhadra + astro calendar events
      const [bhadraRes, astroRes] = await Promise.allSettled([
        authFetch(`${getApiBase()}/bhadra?month=${mo}&year=${yr}`).then(r => r.json()),
        authFetch(`${getApiBase()}/astro-calendar/calendar-data?month=${mo}&year=${yr}`).then(r => r.json()),
      ]);
      const bhadra     = bhadraRes.status === 'fulfilled' ? (bhadraRes.value.bhadra || []) : [];
      const astroEvents = astroRes.status === 'fulfilled'  ? (astroRes.value.data   || {}) : {};

      // 2. Per-day: panchangam + moon phase + sun degree (all days in parallel)
      const dayPromises = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const ds = `${yr}-${String(mo).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        dayPromises.push(
          Promise.allSettled([
            authFetch(`${getApiBase()}/panchangam/date/${ds}`).then(r => r.json()),
            authFetch(`${getApiBase()}/moon-phase/date/${ds}`).then(r => r.json()),
            authFetch(`${getApiBase()}/sun-degree/check?date=${ds}`).then(r => r.json()),
          ]).then(([pRes, mRes, sRes]) => ({
            dateStr: ds,
            panchangam: pRes.status === 'fulfilled' ? pRes.value : null,
            moon:       mRes.status === 'fulfilled' ? mRes.value : null,
            sunDegree:  sRes.status === 'fulfilled' ? sRes.value : null,
          }))
        );
      }

      const dayResults = await Promise.all(dayPromises);
      const newDayData = {};
      const sunDegreeReversals = [];
      dayResults.forEach(r => {
        newDayData[r.dateStr] = r;
        if (r.sunDegree?.isReversal) sunDegreeReversals.push({ dateStr: r.dateStr, ...r.sunDegree });
      });

      setMonthData({ bhadra, astroEvents, sunDegreeReversals });
      setDayDataCache(newDayData);
    };

    load().catch(err => console.error('Error loading month data:', err));
  }, [viewDate]);

  // ── Select date — reads from cache, no extra fetch ─────────────
  const handleSelectDate = useCallback((date) => {
    setSelectedDate(date);
    const dateStr = fmtDate(date);
    const cached  = dayDataCache[dateStr];

    if (cached) {
      setDetailData({
        panchangam:  cached.panchangam,
        moonPhase:   cached.moon,
        sunDegree:   cached.sunDegree,
        bhadra:      monthData.bhadra,
        astroEvents: monthData.astroEvents[dateStr] || { events: [], festivals: [] },
      });
      setDetailState('data');
    } else {
      // Fallback fetch for dates outside the cached month
      setDetailState('loading');
      Promise.allSettled([
        authFetch(`${getApiBase()}/panchangam/date/${dateStr}`).then(r => r.json()),
        authFetch(`${getApiBase()}/moon-phase/date/${dateStr}`).then(r => r.json()),
        authFetch(`${getApiBase()}/sun-degree/check?date=${dateStr}`).then(r => r.json()),
      ]).then(([pRes, mRes, sRes]) => {
        setDetailData({
          panchangam:  pRes.status === 'fulfilled' ? pRes.value : null,
          moonPhase:   mRes.status === 'fulfilled' ? mRes.value : null,
          sunDegree:   sRes.status === 'fulfilled' ? sRes.value : null,
          bhadra:      monthData.bhadra,
          astroEvents: monthData.astroEvents[dateStr] || { events: [], festivals: [] },
        });
        setDetailState('data');
      }).catch(() => setDetailState('error'));
    }
  }, [dayDataCache, monthData]);

  // Keep the selected day's details in sync as monthly data finishes loading.
  useEffect(() => {
    if (!selectedDate) return;

    const dateStr = fmtDate(selectedDate);
    const cached = dayDataCache[dateStr];
    if (!cached) return;

    setDetailData({
      panchangam:  cached.panchangam,
      moonPhase:   cached.moon,
      sunDegree:   cached.sunDegree,
      bhadra:      monthData.bhadra,
      astroEvents: monthData.astroEvents[dateStr] || { events: [], festivals: [] },
    });
    setDetailState('data');
  }, [selectedDate, dayDataCache, monthData]);

  // ── Calendar cells ─────────────────────────────────────────────
  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDayOfWeek  = new Date(year, month, 1).getDay();
  const daysInMonth     = new Date(year, month + 1, 0).getDate();
  const prevDaysInMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    cells.push({ day: prevDaysInMonth - i, month: month - 1, year, otherMonth: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, otherMonth: false });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, month: month + 1, year, otherMonth: true });
  }

  // ── Navigation ─────────────────────────────────────────────────
  const changeMonth = (delta) =>
    setViewDate(d => new Date(d.getFullYear(), d.getMonth() + delta, 1));

  const goToToday = () => {
    setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
    handleSelectDate(today);
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <AppLayout pageBadge="Astro Insight">
      <div className="ua-container">
        <div className="ua-main-layout">

          {/* Calendar */}
          <div className="ua-calendar-section">
            <div className="ua-calendar-controls">
              <div className="ua-nav-controls">
                <button className="ua-nav-btn" onClick={() => changeMonth(-1)}>◀</button>
                <div className="ua-current-month">{MONTH_NAMES[month]} {year}</div>
                <button className="ua-nav-btn" onClick={() => changeMonth(1)}>▶</button>
              </div>
              <button className="ua-today-btn" onClick={goToToday}>Today</button>
            </div>

            <div className="ua-calendar-grid">
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                <div key={d} className={`ua-weekday-header${d === 'Sun' ? ' sunday' : ''}`}>{d}</div>
              ))}

              {cells.map((cell, idx) => {
                const dateStr = cell.otherMonth ? null
                  : `${cell.year}-${String(cell.month+1).padStart(2,'0')}-${String(cell.day).padStart(2,'0')}`;

                const isToday = !cell.otherMonth &&
                  cell.year  === today.getFullYear() &&
                  cell.month === today.getMonth()    &&
                  cell.day   === today.getDate();

                const isSelected = !!(selectedDate && !cell.otherMonth &&
                  cell.year  === selectedDate.getFullYear() &&
                  cell.month === selectedDate.getMonth()    &&
                  cell.day   === selectedDate.getDate());

                return (
                  <DayCell
                    key={`${cell.year}-${cell.month}-${cell.day}-${idx}`}
                    day={cell.day}
                    month={cell.month}
                    year={cell.year}
                    otherMonth={cell.otherMonth}
                    isToday={isToday}
                    isSelected={isSelected}
                    nakshatraTypes={nakshatraTypes}
                    monthData={monthData}
                    dayData={dateStr ? dayDataCache[dateStr] : null}
                    onClick={handleSelectDate}
                  />
                );
              })}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="ua-detail-panel">
            {detailState === 'empty'   && <DetailEmpty />}
            {detailState === 'loading' && <DetailLoading date={selectedDate} />}
            {detailState === 'data'    && detailData && (
              <DetailContent date={selectedDate} {...detailData} />
            )}
            {detailState === 'error'   && (
              <div className="ua-empty-state">
                <div className="ua-empty-icon">❌</div>
                <p>Failed to load data. Please try again.</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </AppLayout>
  );
};

export default AstroInsight;
